import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ChevronRight, Facebook, Twitter, Youtube, Instagram } from "lucide-react";
import BrandMarks from "./BrandMarks";
import { hqContact, socialLinks } from "../data/mock";
import VisitorCounter from "./VisitorCounter";
import { useT } from "../i18n";

const FLink = ({ to, children }) => (
  <Link to={to} className="f-link"><span className="chev"><ChevronRight size={11} /></span>{children}</Link>
);

export default function Footer() {
  const { t } = useT();
  return (
    <footer data-testid="site-footer" style={{ marginTop: 80, position: "relative", overflow: "hidden" }}>
      <div style={{ background: "linear-gradient(180deg, #F97316 0%, #EA580C 100%)", color: "#fff", position: "relative" }} className="grain">
        <div style={{ position: "absolute", right: -120, top: -100, width: 460, height: 460, borderRadius: "50%", background: "radial-gradient(circle, rgba(250,204,21,0.35), transparent 60%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 1440, margin: "0 auto", padding: "54px 24px 40px", position: "relative", display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1.2fr", gap: 40 }} className="footer-grid">
          <div>
            <div className="footer-logo-wrap">
              <BrandMarks logoSize={80} plate />
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.75, textAlign: "justify", color: "rgba(255,255,255,0.95)" }}>
              The Andhra Pradesh Medical Education &amp; Research Corporation revitalises tertiary hospitals, strengthens existing medical institutions and builds new medical colleges — ensuring affordable, world-class healthcare and medical education for every district of Andhra Pradesh.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              {[Facebook, Twitter, Youtube, Instagram].map((I, i) => (
                <a key={i} href={socialLinks[i].url} target="_blank" rel="noreferrer" aria-label={socialLinks[i].name} data-testid={`social-${i}`} style={{ width: 48, height: 48, background: "#fff", color: "#1C1917", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, transition: "transform 200ms, background 200ms" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.background = "#FEF08A"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.background = "#fff"; }}>
                  <I size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: 17, fontWeight: 700, letterSpacing: 1.5, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.7)", marginBottom: 16 }}>{t("f.links")}</h4>
            <div style={{ display: "grid", gap: 4 }}>
              <FLink to="/">Home</FLink>
              <FLink to="/about">About Us</FLink>
              <FLink to="/colleges">College Corner</FLink>
              <FLink to="/admissions">Student Registration</FLink>
              <FLink to="/circulars">Circulars &amp; GOs</FLink>
              <FLink to="/careers">Careers</FLink>
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: 17, fontWeight: 700, letterSpacing: 1.5, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.7)", marginBottom: 16 }}>{t("f.other")}</h4>
            <div style={{ display: "grid", gap: 4 }}>
              <FLink to="/gallery">Gallery</FLink>
              <FLink to="/useful-links">Useful Links</FLink>
              <FLink to="/login">Department Login</FLink>
              <FLink to="/contact">Contact Us</FLink>
              <a className="f-link" href="https://www.ap.gov.in/" target="_blank" rel="noreferrer"><span className="chev"><ChevronRight size={11} /></span>AP Government Portal</a>
              <a className="f-link" href="https://www.nmc.org.in/" target="_blank" rel="noreferrer"><span className="chev"><ChevronRight size={11} /></span>National Medical Commission</a>
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: 17, fontWeight: 700, letterSpacing: 1.5, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.7)", marginBottom: 16 }}>{t("f.info")}</h4>
            <div style={{ fontSize: 14, lineHeight: 1.7, display: "grid", gap: 12 }}>
              <div style={{ display: "flex", gap: 10 }}><Phone size={16} style={{ marginTop: 4, flexShrink: 0 }} /> <span>Call Us: {hqContact.phone}</span></div>
              <div style={{ display: "flex", gap: 10 }}><Mail size={16} style={{ marginTop: 4, flexShrink: 0 }} /> <span>Email Us: {hqContact.email}</span></div>
              <div style={{ display: "flex", gap: 10 }}><MapPin size={16} style={{ marginTop: 4, flexShrink: 0 }} /> <span>{hqContact.address}</span></div>
            </div>
            <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t("f.visitors")}:</div>
              <VisitorCounter />
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: "#fff", position: "relative" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", minHeight: 50, fontSize: 13.5, position: "relative", zIndex: 1 }}>
          <div style={{ color: "#1C1917", fontWeight: 600 }}>Copyright © {new Date().getFullYear()} Government of Andhra Pradesh · APMERCL</div>
          <div data-testid="developed-by" className="footer-bottom" style={{ display: "flex", gap: 22, fontWeight: 600, color: "#44403C", paddingRight: "clamp(0px, 16vw, 300px)", flexWrap: "wrap" }}>
            <span>Act No. 7 of 2021</span>
            <span>{t("f.dev")} <a href="https://www.swichglobal.com/" target="_blank" rel="noreferrer" style={{ color: "#EA580C", textDecoration: "none", fontWeight: 800 }}>Swich Global Private Limited</a></span>
          </div>
        </div>
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "18%", background: "linear-gradient(180deg,#F97316,#EA580C)", clipPath: "polygon(18% 0, 100% 0, 100% 100%, 0 100%)" }} className="hide-mobile" />
      </div>

      <style>{`
        @media (max-width: 1000px) { .footer-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 620px) { .footer-grid { grid-template-columns: 1fr !important; } .footer-bottom { padding-right: 4.75rem !important; } .footer-slant { display: none !important; } }
      `}</style>
    </footer>
  );
}
